// CTV-440: combined "Edit campaign" modal. Replaces the standalone Extend +
// Cancel pills for currently-delivering campaigns. Three tabs (Extend, Swap
// creative, Cancel) render their content inline — the modal supplies the
// chrome (backdrop, dialog box, close, tab strip, recent-edits footer) and
// each tab is an overlay-less form component.

import React from 'react';
import { X } from 'lucide-react';
import type { PublisherConfig, SelfServiceSubmissionRow } from '../../types/selfService';
import { apiClient } from '../../lib/apiClient';
import { cancelCopyFor } from './campaignSharedHelpers';
import { CancelCampaignPanel } from './CancelCampaignModal';
import { ExtendPanel } from './campaignActions';
import { useAuth } from './auth/useAuth';
import EditCommercialTabPanel from './EditCreativesTabPanel';

type Tab = 'extend' | 'swap' | 'cancel';

interface EditCampaignModalProps {
  submission: SelfServiceSubmissionRow;
  cfg: PublisherConfig;
  defaultTab?: Tab;
  onClose: () => void;
  onExtended: (id: string, newEndDate: string, additionalBudget: number) => void;
  onCanceled: (id: string, canceledAt: string) => void;
  onCreativeSwapped: () => void;
}

interface EditRow {
  id: string;
  edit_kind: 'extend' | 'creative_swap';
  status: 'pending_review' | 'ready' | 'applied' | 'failed' | 'voided';
  previous_end_date: string | null;
  new_end_date: string | null;
  additional_budget_cents: number | null;
  rejection_reason: string | null;
  created_at: string;
  applied_at: string | null;
}

/**
 * CTV-473: a submission can be extended only when its status is `approved`
 * AND its campaign end date is still in the future. Canceled / refunded /
 * payment-failed campaigns can't take an extension because their downstream
 * Publica line items are no longer scheduled to serve. Both upfront and
 * monthly payment plans are eligible. See ADR-0005.
 */
function canExtendSubmission(
  submission: SelfServiceSubmissionRow,
  endDate: string | undefined,
): boolean {
  if (submission.status !== 'approved') return false;
  if (!endDate) return false;
  // String comparison on YYYY-MM-DD is correct lexicographically.
  const today = new Date().toISOString().slice(0, 10);
  return endDate >= today;
}

export function EditCampaignModal({
  submission,
  cfg,
  defaultTab = 'extend',
  onClose,
  onExtended,
  onCanceled,
  onCreativeSwapped,
}: EditCampaignModalProps) {
  const accent = cfg.accentColor || cfg.primaryColor;
  // CTV-473: Extend is now available for both upfront and monthly. The tab
  // hides only when the campaign can't actually take an extension — finished,
  // canceled, or refunded. ExtendPanel branches internally on payment_plan to
  // render the upfront vs. monthly UX. See ADR-0005.
  const currentEnd = submission.campaign?.endDate;
  const canExtend = React.useMemo(
    () => canExtendSubmission(submission, currentEnd),
    [submission, currentEnd],
  );
  const tabs = React.useMemo(
    () => (canExtend
      ? ([['extend', 'Extend'], ['swap', 'Edit commercial'], ['cancel', 'Cancel']] as const)
      : ([['swap', 'Edit commercial'], ['cancel', 'Cancel']] as const)),
    [canExtend],
  );
  const initialTab: Tab = !canExtend && defaultTab === 'extend' ? 'swap' : defaultTab;
  const [tab, setTab] = React.useState<Tab>(initialTab);
  const [edits, setEdits] = React.useState<EditRow[]>([]);
  const { selectedAdvertiser } = useAuth();

  React.useEffect(() => {
    let cancelled = false;
    apiClient
      .get<{ rows: EditRow[] }>(`/submissions/${submission.id}/edits`)
      .then((res) => {
        if (!cancelled) setEdits(res.rows);
      })
      .catch(() => {
        // Edits log is informational; ignore failures.
      });
    return () => {
      cancelled = true;
    };
  }, [submission.id]);

  const cancelVariant = cancelCopyFor(submission);

  return (
    <>
      <div
        className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] z-50"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      />
      <div
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92%] max-w-3xl bg-white rounded-2xl border border-slate-200/80 max-h-[92vh] overflow-y-auto"
        style={{ boxShadow: '0 24px 48px -16px rgba(15,23,42,0.28), 0 2px 4px -1px rgba(15,23,42,0.05)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-[3px]"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
        <div className="flex items-start justify-between p-5 pb-3">
          <div>
            <h2 className="text-[16px] font-semibold tracking-tight text-slate-900 leading-tight">
              Edit campaign
            </h2>
            <p className="text-[12px] text-slate-500 mt-0.5">
              {submission.campaign?.campaignName ?? 'Campaign'} · {cfg.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 -mr-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-1 px-5 border-b border-slate-100">
          {tabs.map(([key, label]) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] border-b-2 transition-colors"
                style={{
                  borderColor: active ? accent : 'transparent',
                  color: active ? accent : '#64748b',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="p-5">
          {tab === 'extend' && (
            currentEnd ? (
              <ExtendPanel
                submission={submission}
                cfg={cfg}
                currentEndDate={currentEnd}
                onClose={onClose}
                onExtended={(newEndDate, addBudget) => {
                  onExtended(submission.id, newEndDate, addBudget);
                  onClose();
                }}
              />
            ) : (
              <p className="text-[13px] text-slate-500">This campaign has no end date to extend.</p>
            )
          )}
          {tab === 'swap' && (
            <EditCommercialTabPanel
              submission={submission}
              cfg={cfg}
              advertiserId={selectedAdvertiser?.advertiserId ?? null}
              onSubmitted={() => {
                onCreativeSwapped();
                onClose();
              }}
            />
          )}
          {tab === 'cancel' && (
            cancelVariant ? (
              <CancelCampaignPanel
                submission={submission}
                cfg={cfg}
                variant={cancelVariant}
                showCloseButton={false}
                onClose={onClose}
                onSuccess={(updated) => {
                  onCanceled(updated.id, updated.canceled_at);
                }}
              />
            ) : (
              <p className="text-[13px] text-slate-500">This campaign can no longer be canceled.</p>
            )
          )}
        </div>

        <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/60 rounded-b-2xl">
          <h3 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-2">Recent edits</h3>
          {edits.length === 0 ? (
            <p className="text-[12px] text-slate-400">No edits yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {edits.slice(-5).reverse().map((e) => (
                <li key={e.id} className="flex items-center justify-between text-[12px]">
                  <span className="text-slate-700">
                    {e.edit_kind === 'extend'
                      ? `Extended to ${e.new_end_date}`
                      : 'Commercial swap'}
                  </span>
                  <span className="text-slate-400 tabular-nums">
                    {e.status} · {new Date(e.created_at).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
